import type {ReactElement} from 'react';
import React from 'react';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {QrBackgroundColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/qr-background-color-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromQRItem} from '@Hooks/poster-editor/useGetKeyFromQRItem';

interface QRBackgroundOptionProps {
  onClickColorFill(): void;
}

enum QRBackgroundDropdownItem {
  TRANSPARENT = 'transparent',
  SOLID = 'solid',
}

export function QRBackgroundOption({...props}: QRBackgroundOptionProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isBgTransparent = useGetKeyFromQRItem('isBackgroundTransparent');

  if (!activeItem || (activeItem && (!('isBackgroundTransparent' in activeItem) || !('qrBackgroundColor' in activeItem))) || isBgTransparent === undefined) {
    throw new Error(`QR Item doesn't have isBackgroundTransparent or qrBackgroundColor property`);
  }
  const getSelectedItemId = (): QRBackgroundDropdownItem => {
    if (isBgTransparent) {
      return QRBackgroundDropdownItem.TRANSPARENT;
    }
    return QRBackgroundDropdownItem.SOLID;
  };

  const onClickHandler = (clickedId: QRBackgroundDropdownItem): void => {
    if (getSelectedItemId() === clickedId) {
      return;
    }
    activeItem.toggleBackgroundStateForQRItem();
  };

  const getColorOptionForBackground = (): ReactElement<any> => {
    return (
      <QrBackgroundColorOption
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (isBgTransparent) {
      return null;
    }
    return getColorOptionForBackground();
  };

  return (
    <PosterEditingBaseDropdownOption
      id="qr-background-option"
      label={window.i18next.t('pmwjs_background')}
      onClick={onClickHandler}
      dropdownItems={[
        {id: QRBackgroundDropdownItem.TRANSPARENT, text: window.i18next.t('pmwjs_transparent')},
        {id: QRBackgroundDropdownItem.SOLID, text: window.i18next.t('pmwjs_solid_color')},
      ]}
      selectedItemId={getSelectedItemId()}
    >
      {getSubOptions()}
    </PosterEditingBaseDropdownOption>
  );
}
